import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { Link } from "react-router-dom";

const TOOLS = [
  { id: "hlr-lookup", name: "HLR Lookup", desc: "Query Home Location Register for any mobile number.", kind: "phone", tag: "PHONE" },
  { id: "carrier-check", name: "Carrier Identifier", desc: "Find the mobile carrier of a phone number instantly.", kind: "phone", tag: "PHONE" },
  { id: "phone-portability", name: "Portability Check", desc: "Detect if a number has been ported to a new network.", kind: "phone", tag: "PHONE" },
  { id: "spam-score", name: "Spam Risk Score", desc: "Real-time spam & scam reputation lookup.", kind: "phone", tag: "PHONE" },
  { id: "dnc-check", name: "DNC Registry Check", desc: "Verify Do Not Call listing status.", kind: "phone", tag: "PHONE" },
  { id: "line-type", name: "Line Type Detector", desc: "Mobile, landline, or VoIP classifier.", kind: "phone", tag: "PHONE" },
  { id: "e164-formatter", name: "E.164 Formatter", desc: "Normalize any phone into E.164 format.", kind: "phone", tag: "PHONE" },

  { id: "disposable-email", name: "Disposable Email Detector", desc: "Flag temporary / throwaway addresses.", kind: "email", tag: "EMAIL" },
  { id: "mx-record-analyzer", name: "MX Record Analyzer", desc: "Inspect a domain's mail exchangers.", kind: "email", tag: "EMAIL" },
  { id: "smtp-check", name: "SMTP Handshake", desc: "Test if an inbox actually accepts mail.", kind: "email", tag: "EMAIL" },
  { id: "email-syntax", name: "Email Syntax Validator", desc: "RFC 5322 compliant parser.", kind: "email", tag: "EMAIL" },
  { id: "role-account", name: "Role Account Finder", desc: "Detect info@, sales@, admin@ addresses.", kind: "email", tag: "EMAIL" },
  { id: "catch-all-detector", name: "Catch-All Detector", desc: "Check if a domain accepts any recipient.", kind: "email", tag: "EMAIL" },
  { id: "free-domain-check", name: "Free Provider Check", desc: "Gmail, Outlook, Yahoo, Proton and more.", kind: "email", tag: "EMAIL" },
  { id: "domain-authority", name: "Domain Authority", desc: "SEO metrics for any domain.", kind: "email", tag: "EMAIL" },

  { id: "ip-geolocation", name: "IP Geolocation", desc: "City-level location for IPv4 & IPv6.", kind: "ip", tag: "IP" },
  { id: "ip-abuse-score", name: "IP Abuse Risk Score", desc: "Aggregated blocklist + threat feed score.", kind: "ip", tag: "IP" },
  { id: "vpn-detector", name: "VPN Detector", desc: "Identify commercial VPN endpoints.", kind: "ip", tag: "IP" },
  { id: "proxy-detector", name: "Proxy Detector", desc: "Residential and datacenter proxies.", kind: "ip", tag: "IP" },
  { id: "tor-detector", name: "Tor Exit Node Check", desc: "Live Tor consensus lookup.", kind: "ip", tag: "IP" },
  { id: "datacenter-check", name: "Datacenter IP Check", desc: "AWS, GCP, Azure and DO range detection.", kind: "ip", tag: "IP" },
  { id: "asn-lookup", name: "ASN Lookup", desc: "Autonomous System Number → org info.", kind: "ip", tag: "IP" },
  { id: "reverse-dns", name: "Reverse DNS", desc: "PTR record lookup for any IP.", kind: "ip", tag: "IP" },
  { id: "whois-lookup", name: "WHOIS Lookup", desc: "Registration data for domains & IPs.", kind: "ip", tag: "IP" },
  { id: "port-scanner", name: "Port Reachability", desc: "Rate-limited port availability probe.", kind: "ip", tag: "IP" },
];

const KIND_COLOR = {
  phone: "text-cyan-signal border-cyan-500/30 bg-cyan-500/5",
  email: "text-emerald-400 border-emerald-500/30 bg-emerald-500/5",
  ip: "text-purple-400 border-purple-500/30 bg-purple-500/5",
};

export default function Tools() {
  const [q, setQ] = useState("");
  const filtered = TOOLS.filter((t) => t.name.toLowerCase().includes(q.toLowerCase()) || t.desc.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="mb-10">
        <span className="font-mono text-xs uppercase tracking-widest text-cyan-signal">Free Tools · Rate-limited</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-display font-bold tracking-tight">25 browser utilities</h1>
        <p className="mt-3 text-slate-400 max-w-2xl">Every free tool is a wrapper around the same Good Guys API — perfect for one-off checks or quick investigations.</p>
      </div>

      <div className="relative max-w-md mb-8">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
        <Input
          data-testid="tools-search-input"
          placeholder="Search tools…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-9 bg-slate-950 border-slate-800 h-11"
        />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((t) => (
          <Link
            key={t.id}
            to="/console"
            data-testid={`tool-card-${t.id}`}
            className="group rounded-xl border border-slate-800 bg-slate-900/40 p-5 hover-lift hover:border-cyan-500/30"
          >
            <div className="flex items-start justify-between mb-3">
              <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-widest border ${KIND_COLOR[t.kind]}`}>{t.tag}</span>
            </div>
            <h3 className="font-display font-semibold text-base group-hover:text-cyan-signal transition-colors">{t.name}</h3>
            <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{t.desc}</p>
            <div className="mt-4 text-xs font-mono text-slate-500 group-hover:text-cyan-signal transition-colors">Open tool →</div>
          </Link>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500 text-sm">No tools match "{q}"</div>
        )}
      </div>
    </div>
  );
}
